import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useNotifier } from "react-headless-notifier"
import useErrorHandler from "hooks/useErrorHandler"
import { useForm } from "react-hook-form"
import { useGetClientsQuery } from "api/endpoints/admin/client"
import { useAdminMassAssignAssetsMutation, useAdminListUsersQuery } from "api"
import NotificationToast from "components/notifications"
import { Client } from "api/types/models/client"
import FormHeader from "components/admin/form_header"
import FormLayout from "components/admin/forms/layouts/FormLayout"
import ComboBox from "components/form/combobox"
import FormError from "components/error"
import Input from "components/form/input"
import TextArea from "components/form/textarea"
import Alert from "components/alerts"
import { getAssetMassAssignErrorString } from "helpers/string"
import { RadioCardGroup } from "components/form/radio_card_group"
import { RadioCardOption } from "components/form/radio_card"
import Button from "components/button"
import { AssetIdentifierType, FriendlyAssetIdentifierType } from "enums/AssetIdentifierType"

type FormInputs = {
  identifier_type: AssetIdentifierType
  client_id?: number
  user_id?: number
  redirect_url?: string
  identifiers: string[]
}

const AdminAssetMassAssignPage = () => {
  const { notify } = useNotifier()
  const navigate = useNavigate()
  const errorHandler = useErrorHandler()

  const [identifiersInput, setIdentifiersInput] = useState('')

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    setError,
    control,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm<FormInputs>({
    defaultValues: {
      identifier_type: AssetIdentifierType.MaskedUid,
      client_id: undefined,
      user_id: undefined,
      redirect_url: '',
      identifiers: [],
    },
  })

  const {
    data: clients,
    isLoading: isClientsLoading,
  } = useGetClientsQuery({})

  const {
    data: users,
    isLoading: isUsersLoading,
  } = useAdminListUsersQuery({
    clientId: watch('client_id'),
  }, {
    skip: !watch('client_id'),
  })

  const [submitMassAssign, {
    data: result,
    error: massAssignError,
    isLoading: isSubmitLoading,
  }] = useAdminMassAssignAssetsMutation()

  const submitWrapper = (data: FormInputs) => {
    data.identifiers = identifiersInput
      .split(/[\s,]+/)
      .map((identifier) => identifier.trim())
      .filter((identifier) => identifier !== '')
    return submitMassAssign(data)
  }

  useEffect(() => {
    if (result) {
      if (result.assets && !result.errors) {
        navigate('/admin/dashboard/assets/browse')
        notify(<NotificationToast message={`You have successfully assigned ${result.assets.length} asset(s)`} />)
      }
    }
  }, [result])

  useEffect(() => {
    if (massAssignError) {
      errorHandler(massAssignError, setError)
    }
  }, [massAssignError])

  const clientOptions = useMemo(() => {
    const unpaginatedClients = clients as Client[]
    return unpaginatedClients?.map((client) => ({ value: client.id, text: client.display_name }))
  }, [clients])

  const identifierTypeOptions = Object.values(AssetIdentifierType)

  return (
    <>
      <div className="flex flex-col h-full">
        <FormHeader
          title="Mass Assign Assets"
          description="Assign a client, owner and optional redirect URL to multiple existing, unregistered assets"
          backBtnText="Back to Assets"
          backBtnUrl="/admin/dashboard/assets/browse"
        />

        <FormLayout>
          <form onSubmit={handleSubmit(submitWrapper)}>
            <FormLayout.Block>
              <FormLayout.Group
                label="Identifier Type"
                htmlFor="identifier_type"
                description="Specify which value you are providing to find the assets"
              >
                <RadioCardGroup name="identifier_type" control={control}>
                  {
                    identifierTypeOptions.map((type, index) => (
                      <RadioCardOption
                        key={type}
                        value={type}
                        first={index === 0}
                        last={index === identifierTypeOptions.length - 1}
                      >
                        {FriendlyAssetIdentifierType[type]}
                      </RadioCardOption>
                    ))
                  }
                </RadioCardGroup>
              </FormLayout.Group>

              <FormLayout.Group
                label="Client"
                htmlFor="client_id"
                description="Assign the assets to this client (also filters the owners below)"
              >
                <ComboBox
                  isLoading={isClientsLoading}
                  onChange={(option) => {
                    if (option) {
                      setValue('client_id', option.value as number)
                      setValue('user_id', undefined)
                    }
                  }}
                  options={clientOptions}
                  placeholder="Search for a client"
                />
                {
                  errors.client_id?.message &&
                  <FormError text={errors?.client_id?.message} className="mt-1 mb-2 !text-left" />
                }
              </FormLayout.Group>

              <FormLayout.Group
                label="Owner"
                htmlFor="user_id"
                description="Assign the assets to this owner for management"
              >
                <ComboBox
                  key={`user-search-${watch('client_id')}`} // Force re-create when client changes as selected state is internal
                  isLoading={isUsersLoading}
                  disabled={!watch('client_id')}
                  placeholder="Search for an owner"
                  onChange={(option) => {
                    if (option) setValue('user_id', option.value as number)
                  }}
                  options={users?.users.map((user) => ({ value: user.id, text: `${user.first_name} ${user.last_name}` }))}
                />
                {
                  errors.user_id?.message &&
                  <FormError text={errors.user_id?.message} className="mt-1 mb-2 !text-left" />
                }
              </FormLayout.Group>

              <FormLayout.Group
                label="Redirect URL"
                htmlFor="redirect_url"
                description="Optionally set a custom URL these assets redirect to when scanned whilst inactive"
              >
                <Input {...register('redirect_url')} placeholder="https://www.example.com" />
                {
                  errors.redirect_url &&
                  <FormError text={errors.redirect_url.message} className="mt-1 mb-2" />
                }
              </FormLayout.Group>

              <FormLayout.Group
                label="Identifiers"
                htmlFor="identifiers"
                description="Input the Masked IDs (Payloads) or Serials of the assets you want to assign, separated by commas or new lines. Up to 1000 assets can be assigned at a time."
              >
                <div className="flex flex-col gap-3">
                  <TextArea
                    value={identifiersInput}
                    onChange={(value) => {
                      setIdentifiersInput(value.currentTarget.value)
                      clearErrors('identifiers')
                    }}
                    placeholder={watch('identifier_type') === AssetIdentifierType.Serial
                      ? "SERIAL001, SERIAL002..."
                      : "abc123, def456..."}
                    className="min-h-[172px] text-sm px-2 py-1"
                  />
                  {
                    errors.identifiers &&
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore - React Hook forms types the array validation as a FieldError[] when the response is actually an object
                    <FormError text={errors.identifiers.message} className="mt-1 mb-2" />
                  }
                  {
                    result?.errors &&
                    <Alert type="error" className="overflow-y-auto max-h-64" message={
                      <div className="-mt-1">
                        <div className="mb-5">
                          <h1 className="mb-1 text-lg font-bold text-red-600">Some assets could not be assigned</h1>
                          <p>{result.errors.length} identifier(s) were skipped</p>
                          <p>The remaining assets were assigned successfully. See below for details.</p>
                        </div>
                        {
                          result.errors.map(({ identifier, data }, index) => (
                            <div key={`mass-assign-error-${index}`}>
                              <h3 className="mb-1 font-semibold text-red-500">{identifier}</h3>
                              <ul className="mb-2 ml-4 list-disc">
                                {
                                  data && data.map((err, dataIndex) => (
                                    <li key={`mass-assign-error-${index}-item-${dataIndex}`}>
                                      {getAssetMassAssignErrorString(err.message)} {err.count > 1 ? `(${err.count})` : ''}
                                    </li>
                                  ))
                                }
                              </ul>
                            </div>
                          ))
                        }
                      </div>
                    } />
                  }
                </div>
              </FormLayout.Group>
            </FormLayout.Block>

            <FormLayout.Footer>
              <Button variant="secondary" onClick={() => {
                reset()
                setIdentifiersInput('')
              }}>
                Clear
              </Button>
              <Button href="/admin/dashboard/assets/browse" variant="secondary" className="hidden lg:block">
                Cancel
              </Button>
              <Button type="submit" isLoading={isSubmitLoading}>Assign</Button>
            </FormLayout.Footer>
          </form>
        </FormLayout>
      </div >
    </>
  )
}

export default AdminAssetMassAssignPage
